import {ISettingsState, SettingsActions} from "./types";
import {Reducer} from "redux";
import {getType} from "typesafe-actions";
import {SceneryType} from "./SceneryType";
import {BonusType} from "../player/types";
import * as actions from "./actions";
import {getViewportDimensions} from "./sagas/viewportDimensions";
import merge from "../../shared/utils/merge";

export const settingsInitialState: ISettingsState = {
    appVersion: "1.0.0",
    allowDebug: false,
    randomSeed: 4, // chosen by fair dice roll. Guaranteed to be random
    levelButtonsPerScreen: 5,

    canvas: getViewportDimensions(800),

    api: {
        endpoints: [
            "https://de-js-api-1.best-quiz.com",
            "https://de-js-api-2.best-quiz.com"
        ],
        uriTemplate: "/api/{section}/{method}",
        requestTimeout: 5000,
        retryCount: 8,
        retryTimeout: 1000,
        maxRetryTimeout: 16000,
        gzipEnabled: true,
        gzipMinLength: 1000,
        gzipLevel: 6,
        socketUriTemplate: "/signalr",
        socketReconnectionTimeout: 2000,
        socketHubName: "apiHub",
    },

    commentsApi: {
        apiKey: "1997425F17FF4F3BA1AD5E6E64E5E250",
        endpoints: [
            "https://de-comments-api-1.best-quiz.com/api",
            "https://de-comments-api-2.best-quiz.com/api",
        ],
        retryCount: 3,
        retryTimeout: 1000,
    },

    performanceMetrics: {
        apiCall: true,
        loadAd: true,
        loadScene: true,
        waitForAds: true,
        gzipEncoder: true,
        synchronize: true,
        gameInitialization: true,
    },

    general: {
        uiLanguage: "English",
        locale: "en-US",
        seasons: Object.values(SceneryType),
        totalLevelScreens: 15,
        loadSceneRetryTimeout: 1000,
        loadSceneRetryCount: 5,
        logActions: false,
        synchronizationTimeout: 30 * 1000, // 30s
        availableLanguages: [
            { title: "English", value: "English", locale: "en-US" }
        ],
        defaultUserNameTemplate: "Player #{userid}",
        defaultUserPictureTemplate: "https://d76thc716k1dh.cloudfront.net/quizzlandavatars/{userid | mod | 244}.png",
    },

    gameBalance: {
        maxLives: 5,
        startingCoins: 600,
        buyMoreTimeCost: 5,
        buyMoreTimeCountdown: 5,
        liveCost: 80,
        restoreLivesInterval: 5 * 60 * 1000,
        hintCost: {
            [BonusType.RevealTile]: 80,
            [BonusType.RevealMap]: 300,
            [BonusType.ShowExit]: 160,
            [BonusType.DoubleChance]: 20,
            [BonusType.FiftyFifty]: 40,
            [BonusType.MajorityVote]: 60,
            [BonusType.SkipQuestion]: 30,
        },
        levelCompleteStars: [
            {
                count: 1,
                minAnsweredQuestions: 1,
            },
            {
                count: 2,
                minAnsweredQuestions: 1,
                rightAnswersPercent: 50,
            },
            {
                count: 3,
                minAnsweredQuestions: 1,
                rightAnswersPercent: 80,
            }
        ],
        coinsForStars: {
            1: 20,
            2: 40,
            3: 100
        },
        secondsForAnswer: 30,
        dailyBonuses: [
            {
                bonus: 10,
                amount: 10,
            },
            {
                bonus: 10,
                amount: 20,
            },
            {
                bonus: 10,
                amount: 50,
            },
            {
                bonus: 10,
                amount: 75,
            },
            {
                bonus: 10,
                amount: 100,
            },
            {
                bonus: 10,
                amount: 150,
            },
        ]
    },

    animation: {
        enabled: true,
        timeScale: 1
    },

    miniGames: {
        chestCount: 5,
        chestsWinRate: 0.67,

        cardCount: 6,
        cardsWinRate: 0.67,
        cardBonuses:  [
            BonusType.DoubleChance, BonusType.FiftyFifty, BonusType.MajorityVote, BonusType.SkipQuestion,
            BonusType.RevealMap, BonusType.RevealTile, BonusType.ShowExit,
            BonusType.Coins
        ],

        bonusCoins: 15, 
        bonusScore: 15,
    },

    sdk: {},

    advertisement: {
        rewardedCoins: 20,
        rewardedAdDelay: 2 * 60 * 1000, // 2min
        interstitialAdDelay: 5 * 60 * 1000, // 5min
        interstitialAfterRewardedDelay: 1000,
        loadSceneTimeout: 30000,
        showAdRetryCount: 3,
        showAdTimeout: 30000
    },

    social: {
        shareImageApiEndpoint: "https://n1.images.quizz.land/data-uri/{template}",
        shareImageTemplate: "quizzland",
        invites: [],
    },

    store: {
        products: [],
        consumeRetryCount: 3,
        consumeRetryTimeout: 500,
    },

    urls: {
        appStoreUrl: "http://itunes.apple.com/app/id1275023828",
        googlePlayUrl: "https://play.google.com/store/apps/details?id=com.xmonetize.quizzland"
    }
};

export const settingsReducer : Reducer<ISettingsState, SettingsActions> = (state = settingsInitialState, action) => {
    switch (action.type) {
        case getType(actions.changeSettings):
            return merge(
                state,
                action.payload,
                {
                    general: {
                        ...state.general,
                        seasons: [SceneryType.Summer]
                    }
                }
            );

        default:
            return state;
    }
};
